import { Link } from "@tanstack/react-router";
import { AlertTriangle, Award, Calendar, Check, ChevronRight, CloudRain, FileText, Home, MapPin, Search, ShieldCheck, Sparkles, Star, Sun, Waves, Wrench } from "lucide-react";
import { SiteHeader } from "./SiteHeader";
import { SiteFooter } from "./SiteFooter";
import { CTABlock } from "./CTABlock";
import { HeroBackground } from "./HeroBackground";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { browardCities, residentialServices, commercialServices } from "@/data/site";
import { getServiceImage } from "@/data/serviceImages";

export type ServicePageProps = {
  category: "residential" | "commercial";
  slug: string;
  title: string;
  h1: string;
  intro: string[];
  included: string[];
  process: { title: string; body: string }[];
  whyHere: string[];
  pricingFactors: string[];
  faq: { q: string; a: string }[];
  related: string[];
  signs?: string[];
  areaNote?: string;
};

// Position-based subheadings to break up long paragraph runs.
const INTRO_SECTIONS = [
  { heading: "The problem we see", Icon: Search },
  { heading: "How we approach it", Icon: Wrench },
  { heading: "What to expect on the day", Icon: Calendar },
  { heading: "What sets us apart", Icon: Award },
  { heading: "Getting a quote", Icon: FileText },
  { heading: "The bottom line", Icon: Sparkles },
];

const WHY_SECTIONS = [
  { heading: "South Florida's climate is brutal on exteriors", Icon: Sun },
  { heading: "Why proper technique matters here", Icon: Wrench },
  { heading: "Coastal vs. inland realities", Icon: Waves },
  { heading: "HOA, insurance, and resale pressure", Icon: ShieldCheck },
  { heading: "Seasonal timing that pays off", Icon: CloudRain },
];

const FALLBACK_SECTION = { heading: "More about this service", Icon: Home };

function SectionCTA({ label = "Get a Free Quote" }: { label?: string }) {
  return (
    <div className="mt-8 flex justify-center">
      <Link
        to="/contact"
        className="inline-flex items-center justify-center rounded-md bg-accent px-6 py-3 text-sm font-bold text-accent-foreground shadow-glow hover:bg-accent/90"
      >
        {label}
      </Link>
    </div>
  );
}

export function ServicePage(p: ServicePageProps) {
  const hub = p.category === "residential" ? residentialServices : commercialServices;
  const related = hub.filter((s) => p.related.includes(s.slug));
  const heroImg = getServiceImage(p.category, p.slug);

  return (
    <div className="flex min-h-screen flex-col">
      <SiteHeader />

      <main className="flex-1">
        {/* Breadcrumbs */}
        <div className="border-b border-border bg-muted/40">
          <div className="mx-auto flex max-w-7xl items-center gap-1.5 px-4 py-3 text-xs text-muted-foreground sm:px-6 lg:px-8">
            <Link to="/" className="hover:text-primary">Home</Link>
            <ChevronRight className="h-3 w-3" />
            <Link to={`/${p.category}`} className="hover:text-primary capitalize">{p.category}</Link>
            <ChevronRight className="h-3 w-3" />
            <span className="text-foreground">{p.title}</span>
          </div>
        </div>

        {/* Hero */}
        <section className="relative overflow-hidden bg-gradient-hero text-primary-foreground">
          {heroImg && <HeroBackground src={heroImg} alt={`${p.title} service in Broward County, Florida`} />}
          <div className="relative mx-auto flex min-h-[85vh] max-w-7xl flex-col justify-center px-4 py-16 sm:px-6 sm:py-20 lg:px-8 lg:py-28">
            <div className="max-w-3xl">
              <h1 className="text-4xl font-extrabold sm:text-5xl lg:text-6xl text-balance">{p.h1}</h1>
              <div className="mt-5 flex items-center gap-1 text-accent">
                {[1,2,3,4,5].map((i) => <Star key={i} className="h-4 w-4 fill-current" />)}
                <span className="ml-2 text-sm text-primary-foreground/80">Trusted by 180+ Broward neighbors</span>
              </div>
              <div className="mt-7 flex flex-wrap gap-3">
                <Link to="/contact" className="rounded-md bg-primary px-6 py-3 text-sm font-bold text-primary-foreground hover:bg-primary/90 shadow-glow">Get Free Quote</Link>
                <Link to={`/${p.category}`} className="rounded-md border border-primary-foreground/40 px-6 py-3 text-sm font-bold hover:bg-primary-foreground hover:text-primary">All {p.category} services</Link>
              </div>
            </div>
          </div>
        </section>

        {/* Intro copy */}
        <section className="mx-auto max-w-4xl px-4 py-14 sm:px-6 lg:px-8">
          <div className="space-y-8 text-base leading-relaxed text-foreground/90">
            {p.intro.map((para, i) => {
              const { heading, Icon } = INTRO_SECTIONS[i] ?? FALLBACK_SECTION;
              return (
                <div key={i}>
                  <h2 className="mb-2 flex items-center gap-2 text-xl font-bold text-primary">
                    <Icon className="h-5 w-5 shrink-0 text-accent" />
                    {heading}
                  </h2>
                  <p>{para}</p>
                </div>
              );
            })}
          </div>
          <SectionCTA />
        </section>

        {/* Included */}
        <section className="bg-muted/40">
          <div className="mx-auto max-w-5xl px-4 py-14 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-extrabold text-primary">What's included</h2>
            <p className="mt-2 text-muted-foreground">Every job comes with the same checklist. No surprises.</p>
            <ul className="mt-6 grid gap-3 sm:grid-cols-2">
              {p.included.map((item, i) => (
                <li key={i} className="flex items-start gap-3 rounded-lg border border-border bg-card p-4">
                  <Check className="mt-0.5 h-5 w-5 shrink-0 text-secondary" />
                  <span className="text-sm">{item}</span>
                </li>
              ))}
            </ul>
            <SectionCTA />
          </div>
        </section>

        {/* Process */}
        <section className="mx-auto max-w-5xl px-4 py-14 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-extrabold text-primary">How it works</h2>
          <div className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {p.process.map((step, i) => (
              <div key={i} className="rounded-xl border border-border bg-card p-5 shadow-sm">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-secondary text-primary font-extrabold">{i+1}</div>
                <h3 className="mt-3 text-base font-bold text-primary">{step.title}</h3>
                <p className="mt-1.5 text-sm text-muted-foreground">{step.body}</p>
              </div>
            ))}
          </div>
          <SectionCTA />
        </section>

        {/* Why South Florida */}
        <section className="bg-gradient-to-br from-secondary/15 via-background to-accent/10">
          <div className="mx-auto max-w-4xl px-4 py-14 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-extrabold text-primary">Why this matters in South Florida</h2>
            <div className="mt-6 space-y-7 text-foreground/90">
              {p.whyHere.map((para, i) => {
                const { heading, Icon } = WHY_SECTIONS[i] ?? FALLBACK_SECTION;
                return (
                  <div key={i}>
                    <h3 className="mb-2 flex items-center gap-2 text-lg font-bold text-primary">
                      <Icon className="h-5 w-5 shrink-0 text-accent" />
                      {heading}
                    </h3>
                    <p>{para}</p>
                  </div>
                );
              })}
            </div>
            <SectionCTA />
          </div>
        </section>

        {/* Warning signs */}
        {p.signs && p.signs.length > 0 && (
          <section className="mx-auto max-w-5xl px-4 py-14 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-extrabold text-primary">Signs it's time to call us</h2>
            <p className="mt-2 text-muted-foreground">If any of these sound familiar, you're overdue.</p>
            <div className="mt-6 grid gap-4 sm:grid-cols-2">
              {p.signs.map((s, i) => (
                <div key={i} className="flex items-start gap-3 rounded-lg border border-border bg-card p-4">
                  <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-accent" />
                  <span className="text-sm">{s}</span>
                </div>
              ))}
            </div>
            <SectionCTA />
          </section>
        )}

        {/* Pricing factors */}
        <section className="mx-auto max-w-5xl px-4 py-14 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-extrabold text-primary">What affects the price</h2>
          <p className="mt-2 text-muted-foreground">Every property is different. These are the things we look at when we quote.</p>
          <ul className="mt-6 grid gap-3 sm:grid-cols-2">
            {p.pricingFactors.map((f, i) => (
              <li key={i} className="flex items-start gap-2 text-sm">
                <span className="mt-1 h-1.5 w-1.5 rounded-full bg-accent" />
                <span>{f}</span>
              </li>
            ))}
          </ul>
          <SectionCTA />
        </section>

        {/* FAQ */}
        <section className="bg-muted/40">
          <div className="mx-auto max-w-3xl px-4 py-14 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-extrabold text-primary">Common questions</h2>
            <Accordion type="single" collapsible className="mt-6">
              {p.faq.map((f, i) => (
                <AccordionItem key={i} value={`q${i}`}>
                  <AccordionTrigger className="text-left text-base font-semibold">{f.q}</AccordionTrigger>
                  <AccordionContent className="text-sm text-muted-foreground">{f.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
            <SectionCTA />
          </div>
        </section>

        {/* Related services */}
        <section className="mx-auto max-w-6xl px-4 py-14 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-extrabold text-primary">Related services</h2>
          <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {related.map((s) => (
              <Link
                key={s.slug}
                to={`/${p.category}/${s.slug}` as string}
                className="group rounded-xl border border-border bg-card p-5 transition-all hover:-translate-y-0.5 hover:border-secondary hover:shadow-elegant"
              >
                <div className="text-sm font-bold text-primary group-hover:text-secondary">{s.title}</div>
                <div className="mt-1 text-xs text-muted-foreground">{s.short}</div>
              </Link>
            ))}
          </div>
          <SectionCTA />
        </section>

        {/* Areas served quick */}
        <section className="bg-muted/40">
          <div className="mx-auto max-w-6xl px-4 py-14 sm:px-6 lg:px-8">
            <h2 className="text-2xl font-extrabold text-primary">We service all of Broward County</h2>
            {p.areaNote && <p className="mt-3 max-w-3xl text-sm text-muted-foreground">{p.areaNote}</p>}
            <div className="mt-4 flex flex-wrap gap-2">
              {browardCities.map((c) => (
                <Link key={c} to="/areas-served" className="rounded-full border border-border bg-card px-3 py-1 text-xs hover:border-secondary hover:text-secondary">{c}</Link>
              ))}
            </div>
            <SectionCTA />
          </div>
        </section>

        <CTABlock />
      </main>

      <SiteFooter />
    </div>
  );
}

export function buildServiceMeta(p: { title: string; description: string; path: string; keywords?: string }) {
  const url = `https://mckay.steadyautogrowthdemo.com${p.path}`;
  const kw = p.keywords ||
    `${p.title.toLowerCase()}, pressure washing Broward County, soft wash South Florida, exterior cleaning Pompano Beach, Coastal Surface Experts`;
  return {
    meta: [
      { title: `${p.title} | Coastal Surface Experts – Broward County` },
      { name: "description", content: p.description },
      { name: "keywords", content: kw },
      { name: "publisher", content: "Coastal Surface Experts" },
      { name: "robots", content: "index, follow, max-image-preview:large, max-snippet:-1" },
      { property: "og:title", content: `${p.title} | Coastal Surface Experts` },
      { property: "og:description", content: p.description },
      { property: "og:type", content: "website" },
      { property: "og:url", content: url },
      { name: "twitter:title", content: `${p.title} | Coastal Surface Experts` },
      { name: "twitter:description", content: p.description },
    ],
    links: [{ rel: "canonical", href: url }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Service",
          name: p.title,
          description: p.description,
          provider: {
            "@type": "LocalBusiness",
            "@id": "https://mckay.steadyautogrowthdemo.com/#business",
            name: "Coastal Surface Experts",
            telephone: "+1 754-253-5827",
            address: {
              "@type": "PostalAddress",
              addressLocality: "Pompano Beach",
              addressRegion: "FL",
              addressCountry: "US",
            },
          },
          areaServed: { "@type": "AdministrativeArea", name: "Broward County, FL" },
          url,
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: "https://mckay.steadyautogrowthdemo.com/" },
            ...p.path.split("/").filter(Boolean).map((seg, i, arr) => ({
              "@type": "ListItem",
              position: i + 2,
              name: seg.replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()),
              item: `https://mckay.steadyautogrowthdemo.com/${arr.slice(0, i + 1).join("/")}`,
            })),
          ],
        }),
      },
    ],
  };
}
